import React from "react";

import Button, { ButtonTypes } from "../../../../components/Button";
import {
  Caption,
  CardHeader,
  CardTitle,
  DefinitionList,
  DetailCard,
  Pill,
  StatusNote,
} from "../../styles";
import { formatAgeHours } from "../../utils/format";
import { JOB_STATE_TONES, JOB_STATUS_LABELS } from "../constants";
import { JobDetailProps } from "../types";
import {
  formatOptionalTime,
  isPreHorizonBlocked,
  rangeOfJob,
  recoveryLabelOf,
  subjectOfJob,
  titleOfJob,
} from "../utils/describeJob";
import { JobFailure } from "./JobFailure";

export const JobDetail: React.FC<JobDetailProps> = ({
  job,
  detailRef,
  isStacked,
  onBackToList,
  recoveryNote,
  isRecovering,
  onRecover,
}) => {
  const {
    kind,
    id,
    state,
    status,
    instrument,
    ageHours,
    attempts,
    maxAttempts,
    finalizationAttempts,
    maxFinalizationAttempts,
    lastAttemptAt,
    nextRetryAt,
    completedAt,
  } = job;
  const hasFinalizationAttempts =
    finalizationAttempts !== null && finalizationAttempts > 0;
  const subtitle =
    kind === "snapshot" ? subjectOfJob(job) : `Price backfill · Job #${id}`;
  const subjectLabel = instrument ? "Instrument" : "User";
  const rangeLabel = kind === "snapshot" ? "Affected month" : "Affected range";
  const recoveryLabel = recoveryLabelOf(job);

  return (
    <DetailCard
      ref={detailRef}
      tabIndex={-1}
      aria-label="Selected background job"
    >
      {isStacked && (
        <Button type={ButtonTypes.link} size="small" onClick={onBackToList}>
          Back to jobs
        </Button>
      )}
      <CardHeader>
        <CardTitle>{titleOfJob(job)}</CardTitle>
        <Pill $tone={JOB_STATE_TONES[state]}>{JOB_STATUS_LABELS[status]}</Pill>
      </CardHeader>
      <Caption>{subtitle}</Caption>
      {isPreHorizonBlocked(job) && (
        <StatusNote $tone="unknown">
          This month is before the price history horizon, so no backfill covers
          it and nothing resumes it automatically.
        </StatusNote>
      )}
      <JobFailure job={job} />
      <DefinitionList>
        <dt>{rangeLabel}</dt>
        <dd>{rangeOfJob(job)}</dd>
        <dt>Attempts</dt>
        <dd>{`${attempts} / ${maxAttempts}`}</dd>
        {hasFinalizationAttempts && (
          <>
            <dt>Finalization attempts</dt>
            <dd>{`${finalizationAttempts} / ${maxFinalizationAttempts}`}</dd>
          </>
        )}
        <dt>{subjectLabel}</dt>
        <dd>{subjectOfJob(job)}</dd>
        <dt>Age</dt>
        <dd>{formatAgeHours(ageHours)}</dd>
        <dt>Last attempt</dt>
        <dd>{formatOptionalTime(lastAttemptAt, "No attempts yet")}</dd>
        <dt>Next retry</dt>
        <dd>{formatOptionalTime(nextRetryAt, "None scheduled")}</dd>
        <dt>Completed</dt>
        <dd>{formatOptionalTime(completedAt, "Not completed")}</dd>
      </DefinitionList>
      {recoveryNote && (
        <StatusNote role="status" $tone={recoveryNote.tone}>
          {recoveryNote.message}
        </StatusNote>
      )}
      {recoveryLabel && (
        <Button
          type={ButtonTypes.primary}
          size="small"
          disabled={isRecovering}
          onClick={() => onRecover(job)}
        >
          {recoveryLabel}
        </Button>
      )}
    </DetailCard>
  );
};
